/**
 * @file VendorDropdown.js
 * @description A single-select dropdown component for selecting vendors.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { fetchVendors } from '../../../services/systemService';
import { Dropdown } from '../../helpers/Dropdown';
import LoadingWrapper from '../../ui/loaders/LoadingWrapper';
import Toast from '../../ui/toasts/Toast';
import { getText } from '../../../i18n';

/**
 * A single-select dropdown component for vendors.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The CSS class name for the component.
 * @param {boolean} [props.inlineLabel=true] - Determines whether the label is displayed inline with the dropdown.
 * @param {number|string} [props.selectedVendor] - The ID of the selected vendor.
 * @param {Function} props.onVendorChange - The callback function to handle changes in selected vendor.
 * @param {string} [props.label="Vendor"] - The label text for the dropdown.
 * @param {boolean} [props.isRequired=false] - Determines whether the dropdown is required.
 * @param {boolean} [props.disabled=false] - Determines whether the dropdown is disabled.
 * @param {string} [props.placeholder="Select a vendor"] - The placeholder text for the dropdown.
 * @param {string} [props.errorMessage] - The error message to display when validation fails.
 * @param {boolean} [props.hideErrorMessage=false] - Determines whether to hide the error message text.
 * @param {number} [props.tabIndex=0] - The tab order for the dropdown element.
 * @returns {JSX.Element} The VendorDropdown component.
 */
const VendorDropdown = ({
    className,
    inlineLabel = true,
    selectedVendor,
    onVendorChange,
    label = 'Vendor',
    isRequired = false,
    disabled = false,
    placeholder = 'Select a vendor',
    errorMessage,
    hideErrorMessage = false,
    tabIndex,
}) => {
    const [vendors, setVendors] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const loadVendors = async () => {
            setIsLoading(true);
            try {
                const vendorData = await fetchVendors();
                setVendors(vendorData);
            } catch (error) {
                console.error('Could not fetch vendors:', error);
                Toast.error(getText('msg_ef492d093e38'));
            } finally {
                setIsLoading(false);
            }
        };

        loadVendors();
    }, []);

    const getDefaultLabel = () => {
        const matchingVendor = vendors.find(
            vendor => vendor.id === selectedVendor,
        );
        return matchingVendor?.name ?? placeholder;
    };

    const vendorOptions = useMemo(
        () => [
            { value: null, label: placeholder },
            ...vendors.map(vendor => ({
                value: vendor.id,
                label: vendor.name,
            })),
        ],
        [vendors, placeholder],
    );

    return (
        <fieldset
            className={`form-group mb-3 ${
                inlineLabel ? 'inline-input' : ''
            } ${className} ${errorMessage ? 'is-invalid' : ''}`}
            style={{ position: 'relative' }}
        >
            <label htmlFor="vendor-dropdown">
                {label}
                {isRequired && <span className="text-danger">*</span>}
            </label>
            <LoadingWrapper isLoading={isLoading}>
                <Dropdown
                    id="vendor-dropdown"
                    defaultValue={getDefaultLabel()}
                    options={vendorOptions}
                    value={selectedVendor}
                    onChange={onVendorChange}
                    disabled={vendors.length === 0 || isLoading || disabled}
                    tabIndex={tabIndex}
                />
            </LoadingWrapper>
            {errorMessage && !hideErrorMessage && (
                <div className="invalid-feedback d-block">{errorMessage}</div>
            )}
        </fieldset>
    );
};

export default VendorDropdown;
